import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import DashboardQuickActions from '../../components/ui/DashboardQuickActions';
import Button from '../../components/ui/Button';
import UpgradeModal from '../../components/ui/UpgradeModal';

import SummaryCard from './components/SummaryCard';
import MonthSelector from './components/MonthSelector';
import CategoryBreakdownChart from './components/CategoryBreakdownChart';
import TrendChart from './components/TrendChart';
import AchievementCard from './components/AchievementCard';
import CategoryDataTable from './components/CategoryDataTable';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { accountService } from '../../services/accountService';
import { categoryService } from '../../services/categoryService';
import { budgetService } from '../../services/budgetService';
import Icon from '../../components/AppIcon';

import { transactionService } from '../../services/transactionService';
import { realtimeService } from '../../services/realtimeService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { formatCurrency } from '../../utils/currency';




const FinancialInsights = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { hasProAccess, isPro, isFree } = useSubscription();
  const [selectedMonth, setSelectedMonth] = useState('2026-01');
  const [chartType, setChartType] = useState('line');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [realtimeChannels, setRealtimeChannels] = useState([]);
  const [activeTab, setActiveTab] = useState('insights');
  const [generating, setGenerating] = useState(false);
  const [reportConfig, setReportConfig] = useState({
    dateFrom: new Date(new Date().getFullYear(), new Date().getMonth(), 1)?.toISOString()?.split('T')?.[0],
    dateTo: new Date()?.toISOString()?.split('T')?.[0],
    selectedAccounts: [],
    selectedTypes: [],
    selectedCategories: [],
    selectedSubcategories: [],
    excludeTransfers: false
  });
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [viewPeriod, setViewPeriod] = useState('monthly');
  const [categorySortBy, setCategorySortBy] = useState('highest');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [budgets, setBudgets] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [advancedFiltersExpanded, setAdvancedFiltersExpanded] = useState(false);

  const getCurrencySymbol = (currency) => {
    return '$';
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const data = await transactionService?.getAll();
        setTransactions(data || []);

        const months = [...new Set(data?.map((t) => t?.date?.substring(0, 7)))]?.sort()?.reverse();
        setAvailableMonths(months?.length > 0 ? months : [selectedMonth]);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        setTransactions([]);
        setAvailableMonths([selectedMonth]);
      } finally {
        setLoading(false);
      }
    };

    const loadInitialData = async () => {
      try {
        const [accountsData, categoriesData] = await Promise.all([
          accountService?.getAll(),
          categoryService?.getAll()
        ]);
        setAccounts(accountsData);
        setCategories(categoriesData);

        if (user?.id) {
          try {
            const budgetData = await budgetService?.getBudgetLimits(user?.id, selectedMonth);
            setBudgets(budgetData || []);
          } catch (err) {
            console.error('Error loading budgets:', err);
          }
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
      }
    };

    if (user) {
      fetchTransactions();
      loadInitialData();
    }
  }, [user, selectedMonth]);

  useEffect(() => {
    if (!user) return;

    const channels = realtimeService?.subscribeToAllFinancialData({
      onTransactionChange: async () => {
        try {
          const data = await transactionService?.getAll();
          setTransactions(data || []);
        } catch (error) {
          console.error('Error reloading transactions:', error);
        }
      },
      onBudgetChange: async () => {
        if (user?.id) {
          try {
            const budgetData = await budgetService?.getBudgetLimits(user?.id, selectedMonth);
            setBudgets(budgetData || []);
          } catch (err) {
            console.error('Error reloading budgets:', err);
          }
        }
      },
      onAccountChange: () => {}
    });

    setRealtimeChannels(channels);

    return () => {
      realtimeService?.unsubscribeAll(channels);
    };
  }, [user, selectedMonth]);

  const getMonthToDateData = () => {
    const now = new Date();
    const currentMonth = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}`;
    const currentDay = now?.getDate();

    const mtdTransactions = transactions?.filter((t) => {
      const tDate = new Date(t?.date);
      return t?.date?.startsWith(currentMonth) && tDate?.getDate() <= currentDay;
    });

    const mtdIncome = mtdTransactions?.filter((t) => t?.type === 'income')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
    const mtdExpenses = mtdTransactions?.filter((t) => t?.type === 'expense' || t?.type === 'payment')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);

    return { mtdIncome, mtdExpenses, mtdNet: mtdIncome - mtdExpenses };
  };

  const getFinancialHealthStatus = () => {
    let monthTransactions = transactions?.filter((t) => t?.date?.startsWith(selectedMonth));
    let income = monthTransactions?.filter((t) => t?.type === 'income')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
    const expenses = monthTransactions?.filter((t) => t?.type === 'expense' || t?.type === 'payment')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
    const savingsRate = income > 0 ? ((income - expenses) / income) * 100 : 0;

    let budgetRisk = 0;
    if (budgets?.length > 0) {
      budgets?.forEach(budget => {
        const categorySpending = monthTransactions?.filter(t => t?.categoryId === budget?.categoryId)?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
        const percentUsed = (categorySpending / budget?.limitAmount) * 100;
        if (percentUsed > 100) budgetRisk++;
      });
    }

    if (savingsRate >= 20 && budgetRisk === 0) return { status: 'On Track', color: 'var(--color-success)', icon: 'CheckCircle2' };
    if (savingsRate >= 10 && budgetRisk <= 1) return { status: 'Caution', color: 'var(--color-warning)', icon: 'AlertTriangle' };
    return { status: 'Needs Attention', color: 'var(--color-error)', icon: 'AlertCircle' };
  };

  // Get filtered transactions for reports
  const getReportTransactions = () => {
    if (activeTab !== 'reports') return [];

    let filtered = transactions?.filter((t) => {
      const tDate = t?.date;
      return tDate >= reportConfig?.dateFrom && tDate <= reportConfig?.dateTo;
    });

    // Exclude transfers if option is enabled
    if (reportConfig?.excludeTransfers) {
      filtered = filtered?.filter((t) => {
        const category = categories?.find(c => c?.id === t?.categoryId);
        const categoryName = category?.name?.toLowerCase()?.trim();
        // Exclude transactions with 'Transfer In' or 'Transfers Out' subcategories
        return categoryName !== 'transfer in' && categoryName !== 'transfers out';
      });
    }

    // Filter by Type (if any types selected)
    if (reportConfig?.selectedTypes?.length > 0) {
      filtered = filtered?.filter((t) => reportConfig?.selectedTypes?.includes(t?.type));
    }

    // Filter by Accounts (if any accounts selected)
    if (reportConfig?.selectedAccounts?.length > 0) {
      filtered = filtered?.filter((t) => reportConfig?.selectedAccounts?.includes(t?.accountId));
    }

    // Filter by Categories (if any categories selected)
    if (reportConfig?.selectedCategories?.length > 0) {
      filtered = filtered?.filter((t) => {
        const category = categories?.find(c => c?.id === t?.categoryId);
        // Check if transaction's category OR its parent category is selected
        return reportConfig?.selectedCategories?.includes(t?.categoryId) || 
               (category?.parentId && reportConfig?.selectedCategories?.includes(category?.parentId));
      });
    }

    // Filter by Subcategories (if any subcategories selected)
    if (reportConfig?.selectedSubcategories?.length > 0) {
      filtered = filtered?.filter((t) => reportConfig?.selectedSubcategories?.includes(t?.categoryId));
    }

    return filtered;
  };

  // Get available categories based on selected types and actual transaction data
  const getAvailableCategories = () => {
    // Get transactions in date range
    let dateFilteredTransactions = transactions?.filter((t) => {
      const tDate = t?.date;
      return tDate >= reportConfig?.dateFrom && tDate <= reportConfig?.dateTo;
    });

    // Apply account filter if selected
    if (reportConfig?.selectedAccounts?.length > 0) {
      dateFilteredTransactions = dateFilteredTransactions?.filter((t) => 
        reportConfig?.selectedAccounts?.includes(t?.accountId)
      );
    }

    // If no types selected, show all categories that have transactions in date range
    if (reportConfig?.selectedTypes?.length === 0) {
      const categoryIds = [...new Set(dateFilteredTransactions?.map(t => t?.categoryId)?.filter(Boolean))];
      // Get parent category IDs (for transactions with subcategories)
      const parentCategoryIds = categoryIds?.map(catId => {
        const cat = categories?.find(c => c?.id === catId);
        return cat?.parentId || catId; // Use parent if exists, otherwise use the category itself
      });
      const uniqueParentIds = [...new Set(parentCategoryIds)];
      return categories?.filter(cat => uniqueParentIds?.includes(cat?.id) && !cat?.parentId);
    }

    // Filter transactions by selected types first
    const typeFilteredTransactions = dateFilteredTransactions?.filter((t) => 
      reportConfig?.selectedTypes?.includes(t?.type)
    );

    // Get unique category IDs from these transactions
    const categoryIds = [...new Set(typeFilteredTransactions?.map(t => t?.categoryId)?.filter(Boolean))];
    
    // Get parent category IDs (for transactions with subcategories)
    const parentCategoryIds = categoryIds?.map(catId => {
      const cat = categories?.find(c => c?.id === catId);
      return cat?.parentId || catId; // Use parent if exists, otherwise use the category itself
    });
    const uniqueParentIds = [...new Set(parentCategoryIds)];
    
    // Return categories that:
    // 1. Have transactions in the filtered set (either directly or through subcategories)
    // 2. Are parent categories (not subcategories)
    // 3. Match the selected types
    return categories?.filter(cat => {
      return uniqueParentIds?.includes(cat?.id) && 
             !cat?.parentId && 
             reportConfig?.selectedTypes?.includes(cat?.type);
    });
  };

  // Get available subcategories based on selected categories
  const getAvailableSubcategories = () => {
    if (reportConfig?.selectedCategories?.length === 0) return [];
    
    // Get transactions that match current filters (date, account, type)
    let filtered = transactions?.filter((t) => {
      const tDate = t?.date;
      if (tDate < reportConfig?.dateFrom || tDate > reportConfig?.dateTo) return false;
      if (reportConfig?.selectedAccounts?.length > 0 && !reportConfig?.selectedAccounts?.includes(t?.accountId)) return false;
      if (reportConfig?.selectedTypes?.length > 0 && !reportConfig?.selectedTypes?.includes(t?.type)) return false;
      return true;
    });

    // Get unique category IDs from filtered transactions
    const subcategoryIds = [...new Set(filtered?.map(t => t?.categoryId)?.filter(Boolean))];
    
    // Return subcategories that:
    // 1. Have transactions in the filtered set
    // 2. Have a parent category
    // 3. Parent is in the selected categories
    return categories?.filter(cat => {
      return subcategoryIds?.includes(cat?.id) && 
             cat?.parentId && 
             reportConfig?.selectedCategories?.includes(cat?.parentId);
    });
  };

  const getSummaryData = () => {
    let monthTransactions = transactions?.filter((t) => t?.date?.startsWith(selectedMonth));

    if (activeTab === 'reports') {
      monthTransactions = getReportTransactions();
    }

    let income = monthTransactions?.filter((t) => t?.type === 'income')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
    const expenses = monthTransactions?.filter((t) => t?.type === 'expense' || t?.type === 'payment')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
    const netSavings = income - expenses;
    const savingsRate = income > 0 ? netSavings / income * 100 : 0;

    const [year, month] = selectedMonth?.split('-')?.map(Number);
    const prevMonth = month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1)?.padStart(2, '0')}`;
    let prevMonthTransactions = transactions?.filter((t) => t?.date?.startsWith(prevMonth));

    if (activeTab === 'reports') {
      // Apply same filters to previous month for comparison
      prevMonthTransactions = prevMonthTransactions?.filter((t) => {
        if (reportConfig?.selectedTypes?.length > 0 && !reportConfig?.selectedTypes?.includes(t?.type)) return false;
        if (reportConfig?.selectedAccounts?.length > 0 && !reportConfig?.selectedAccounts?.includes(t?.accountId)) return false;
        if (reportConfig?.selectedCategories?.length > 0 && !reportConfig?.selectedCategories?.includes(t?.categoryId)) return false;
        if (reportConfig?.selectedSubcategories?.length > 0 && !reportConfig?.selectedSubcategories?.includes(t?.categoryId)) return false;
        return true;
      });
    }

    const prevIncome = prevMonthTransactions?.filter((t) => t?.type === 'income')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
    const prevExpenses = prevMonthTransactions?.filter((t) => t?.type === 'expense' || t?.type === 'payment')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
    const prevNetSavings = prevIncome - prevExpenses;

    const incomeTrend = prevIncome > 0 ? (income - prevIncome) / prevIncome * 100 : 0;
    const expenseTrend = prevExpenses > 0 ? (expenses - prevExpenses) / prevExpenses * 100 : 0;
    const savingsTrend = prevNetSavings !== 0 ? (netSavings - prevNetSavings) / Math.abs(prevNetSavings) * 100 : 0;

    const mtdData = getMonthToDateData();

    return [
      {
        title: 'Total Income',
        amount: income,
        currency: getCurrencySymbol('BBD'),
        trend: incomeTrend >= 0 ? 'up' : 'down',
        trendValue: Math.abs(incomeTrend),
        icon: 'TrendingUp',
        iconColor: 'var(--color-success)',
        mtdAmount: mtdData?.mtdIncome,
        lastMonthAmount: prevIncome
      },
      {
        title: 'Total Expenses',
        amount: expenses,
        currency: getCurrencySymbol('BBD'),
        trend: expenseTrend <= 0 ? 'up' : 'down',
        trendValue: Math.abs(expenseTrend),
        icon: 'TrendingDown',
        iconColor: 'var(--color-error)',
        mtdAmount: mtdData?.mtdExpenses,
        lastMonthAmount: prevExpenses
      },
      {
        title: 'Net Savings',
        amount: netSavings,
        currency: getCurrencySymbol('BBD'),
        trend: savingsTrend >= 0 ? 'up' : 'down',
        trendValue: Math.abs(savingsTrend),
        icon: 'Wallet',
        iconColor: 'var(--color-primary)',
        mtdAmount: mtdData?.mtdNet,
        lastMonthAmount: prevNetSavings,
        netChange: netSavings - prevNetSavings,
        netChangePercent: prevNetSavings !== 0 ? ((netSavings - prevNetSavings) / Math.abs(prevNetSavings)) * 100 : 0
      },
      {
        title: 'Savings Rate',
        amount: savingsRate,
        currency: '',
        trend: savingsRate >= 10 ? 'up' : 'down',
        trendValue: Math.abs(savingsRate),
        icon: 'Percent',
        iconColor: 'var(--color-accent)'
      }
    ];
  };

  const getCategoryData = () => {
    let monthTransactions = transactions?.filter((t) => t?.date?.startsWith(selectedMonth));

    if (activeTab === 'reports') {
      monthTransactions = getReportTransactions();
    }

    const totalExpenses = monthTransactions?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);

    const [year, month] = selectedMonth?.split('-')?.map(Number);
    const prevMonth = month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1)?.padStart(2, '0')}`;
    let prevMonthTransactions = transactions?.filter((t) => t?.date?.startsWith(prevMonth));

    if (activeTab === 'reports') {
      prevMonthTransactions = prevMonthTransactions?.filter((t) => {
        if (reportConfig?.selectedTypes?.length > 0 && !reportConfig?.selectedTypes?.includes(t?.type)) return false;
        if (reportConfig?.selectedAccounts?.length > 0 && !reportConfig?.selectedAccounts?.includes(t?.accountId)) return false;
        if (reportConfig?.selectedCategories?.length > 0 && !reportConfig?.selectedCategories?.includes(t?.categoryId)) return false;
        if (reportConfig?.selectedSubcategories?.length > 0 && !reportConfig?.selectedSubcategories?.includes(t?.categoryId)) return false;
        return true;
      });
    }

    const categoryMap = {};

    monthTransactions?.forEach((t) => {
      const categoryName = t?.category || 'Uncategorized';
      const categoryColor = categories?.find(c => c?.id === t?.categoryId)?.color || '#6366f1';
      if (!categoryMap?.[categoryName]) {
        categoryMap[categoryName] = { value: 0, color: categoryColor, transactions: 0 };
      }
      categoryMap[categoryName].value += Math.abs(t?.amount || 0);
      categoryMap[categoryName].transactions += 1;
    });

    const prevCategoryMap = {};
    prevMonthTransactions?.forEach((t) => {
      const categoryName = t?.category || 'Uncategorized';
      if (!prevCategoryMap?.[categoryName]) {
        prevCategoryMap[categoryName] = 0;
      }
      prevCategoryMap[categoryName] += Math.abs(t?.amount || 0);
    });

    let categoryData = Object.keys(categoryMap)?.map((name) => {
      const value = categoryMap?.[name]?.value;
      const prevValue = prevCategoryMap?.[name] || 0;
      const change = prevValue > 0 ? ((value - prevValue) / prevValue) * 100 : 0;
      const percentage = totalExpenses > 0 ? (value / totalExpenses) * 100 : 0;

      let insight = '';
      if (change > 20) insight = 'Significant increase from last month';
      else if (change < -20) insight = 'Notable decrease from last month';
      else if (percentage > 30) insight = 'Major spending category';

      return {
        name,
        value,
        percentage,
        change,
        color: categoryMap?.[name]?.color,
        transactions: categoryMap?.[name]?.transactions,
        trend: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
        insight
      };
    });

    if (categorySortBy === 'highest') {
      categoryData = categoryData?.sort((a, b) => b?.value - a?.value);
    } else if (categorySortBy === 'growth') {
      categoryData = categoryData?.sort((a, b) => b?.change - a?.change);
    } else if (categorySortBy === 'risk') {
      categoryData = categoryData?.sort((a, b) => {
        const aBudget = budgets?.find(bud => {
          const cat = categories?.find(c => c?.name === a?.name);
          return cat?.id === bud?.categoryId;
        });
        const bBudget = budgets?.find(bud => {
          const cat = categories?.find(c => c?.name === b?.name);
          return cat?.id === bud?.categoryId;
        });
        const aRisk = aBudget ? (a?.value / aBudget?.limitAmount) * 100 : 0;
        const bRisk = bBudget ? (b?.value / bBudget?.limitAmount) * 100 : 0;
        return bRisk - aRisk;
      });
    }

    return categoryData;
  };

  // Get subcategory breakdown for reports
  const getSubcategoryData = () => {
    if (activeTab !== 'reports' || reportConfig?.selectedSubcategories?.length === 0) return [];

    const reportTransactions = getReportTransactions();
    const subcategoryMap = {};

    reportTransactions?.forEach((t) => {
      const category = categories?.find(c => c?.id === t?.categoryId);
      if (category && category?.parentId) {
        const subcategoryName = category?.name;
        const parentCategory = categories?.find(c => c?.id === category?.parentId);
        const parentName = parentCategory?.name || 'Unknown';
        
        if (!subcategoryMap?.[subcategoryName]) {
          subcategoryMap[subcategoryName] = {
            name: subcategoryName,
            parent: parentName,
            value: 0,
            transactions: 0,
            color: category?.color || '#6366f1'
          };
        }
        subcategoryMap[subcategoryName].value += Math.abs(t?.amount || 0);
        subcategoryMap[subcategoryName].transactions += 1;
      }
    });

    return Object.values(subcategoryMap)?.sort((a, b) => b?.value - a?.value);
  };

  // Get top merchants
  const getTopMerchants = () => {
    if (activeTab !== 'reports') return [];

    const reportTransactions = getReportTransactions();
    const merchantMap = {};

    reportTransactions?.forEach((t) => {
      const merchant = t?.description || 'Unknown';
      if (!merchantMap?.[merchant]) {
        merchantMap[merchant] = {
          name: merchant,
          amount: 0,
          transactions: 0
        };
      }
      merchantMap[merchant].amount += Math.abs(t?.amount || 0);
      merchantMap[merchant].transactions += 1;
    });

    return Object.values(merchantMap)
      ?.sort((a, b) => b?.amount - a?.amount)
      ?.slice(0, 10);
  };

  const getTrendData = () => {
    const [year, month] = selectedMonth?.split('-')?.map(Number);
    let periods = [];

    if (viewPeriod === 'monthly') {
      for (let i = 5; i >= 0; i--) {
        let m = month - i;
        let y = year;
        while (m <= 0) {
          m += 12;
          y -= 1;
        }
        periods?.push({ month: `${y}-${String(m)?.padStart(2, '0')}`, label: new Date(y, m - 1)?.toLocaleDateString('en-US', { month: 'short' }) });
      }
    } else if (viewPeriod === 'quarterly') {
      for (let i = 3; i >= 0; i--) {
        const quarterMonth = month - (i * 3);
        let m = quarterMonth;
        let y = year;
        while (m <= 0) {
          m += 12;
          y -= 1;
        }
        periods?.push({ month: `${y}-${String(m)?.padStart(2, '0')}`, label: `Q${Math.ceil(m / 3)} ${y}` });
      }
    } else {
      for (let m = 1; m <= month; m++) {
        periods?.push({ month: `${year}-${String(m)?.padStart(2, '0')}`, label: new Date(year, m - 1)?.toLocaleDateString('en-US', { month: 'short' }) });
      }
    }

    return periods?.map(({ month: periodMonth, label }) => {
      let periodTransactions = transactions?.filter((t) => t?.date?.startsWith(periodMonth));

      if (activeTab === 'reports') {
        periodTransactions = periodTransactions?.filter((t) => {
          if (reportConfig?.selectedTypes?.length > 0 && !reportConfig?.selectedTypes?.includes(t?.type)) return false;
          if (reportConfig?.selectedAccounts?.length > 0 && !reportConfig?.selectedAccounts?.includes(t?.accountId)) return false;
          if (reportConfig?.selectedCategories?.length > 0 && !reportConfig?.selectedCategories?.includes(t?.categoryId)) return false;
          if (reportConfig?.selectedSubcategories?.length > 0 && !reportConfig?.selectedSubcategories?.includes(t?.categoryId)) return false;
          return true;
        });
      }

      let income = 0;
      let expense = 0;

      income = periodTransactions?.filter((t) => t?.type === 'income')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
      expense = periodTransactions?.filter((t) => t?.type === 'expense' || t?.type === 'payment')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);

      return { month: label, income, expense };
    });
  };

  const getIncomeStability = () => {
    const last6Months = [];
    const [year, month] = selectedMonth?.split('-')?.map(Number);

    for (let i = 5; i >= 0; i--) {
      let m = month - i;
      let y = year;
      while (m <= 0) {
        m += 12;
        y -= 1;
      }
      const monthStr = `${y}-${String(m)?.padStart(2, '0')}`;
      const monthIncome = transactions?.filter(t => t?.date?.startsWith(monthStr) && t?.type === 'income')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
      last6Months?.push(monthIncome);
    }

    const avgIncome = last6Months?.reduce((sum, val) => sum + val, 0) / last6Months?.length;
    const variance = last6Months?.reduce((sum, val) => sum + Math.pow(val - avgIncome, 2), 0) / last6Months?.length;
    const stdDev = Math.sqrt(variance);
    const coefficientOfVariation = avgIncome > 0 ? (stdDev / avgIncome) * 100 : 0;

    if (coefficientOfVariation < 10) return { status: 'Stable', color: 'var(--color-success)', icon: 'TrendingUp' };
    if (coefficientOfVariation < 25) return { status: 'Moderate', color: 'var(--color-warning)', icon: 'Activity' };
    return { status: 'Volatile', color: 'var(--color-error)', icon: 'TrendingDown' };
  };

  const getCreditUsageTrend = () => {
    const creditAccounts = accounts?.filter(a => a?.type === 'Credit Card');
    if (creditAccounts?.length === 0) return null;

    const last3Months = [];
    const [year, month] = selectedMonth?.split('-')?.map(Number);

    for (let i = 2; i >= 0; i--) {
      let m = month - i;
      let y = year;
      while (m <= 0) {
        m += 12;
        y -= 1;
      }
      const monthStr = `${y}-${String(m)?.padStart(2, '0')}`;
      const monthCreditSpending = transactions?.filter(t => {
        const account = accounts?.find(a => a?.id === t?.accountId);
        return t?.date?.startsWith(monthStr) && account?.type === 'Credit Card' && t?.type === 'payment';
      })?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
      last3Months?.push(monthCreditSpending);
    }

    const trend = last3Months?.[2] > last3Months?.[0] ? 'increasing' : last3Months?.[2] < last3Months?.[0] ? 'decreasing' : 'stable';
    return {
      trend,
      color: trend === 'decreasing' ? 'var(--color-success)' : trend === 'increasing' ? 'var(--color-error)' : 'var(--color-warning)',
      icon: trend === 'decreasing' ? 'TrendingDown' : trend === 'increasing' ? 'TrendingUp' : 'Minus'
    };
  };

  const getAchievements = () => {
    const totalTransactions = transactions?.length || 0;
    const monthsWithIncome = [...new Set(
      transactions?.filter((t) => t?.type === 'income')?.map((t) => t?.date?.substring(0, 7))
    )]?.length;

    const categorizedTransactions = transactions?.filter((t) => t?.category && t?.category !== 'Uncategorized')?.length || 0;

    const [year, month] = selectedMonth?.split('-')?.map(Number);
    let savingsMonthCount = 0;
    let budgetAdherenceCount = 0;

    for (let i = 0; i < 3; i++) {
      let m = month - i;
      let y = year;
      while (m <= 0) {
        m += 12;
        y -= 1;
      }

      const monthStr = `${y}-${String(m)?.padStart(2, '0')}`;
      let monthTransactions = transactions?.filter((t) => t?.date?.startsWith(monthStr));

      let income = monthTransactions?.filter((t) => t?.type === 'income')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
      const expenses = monthTransactions?.filter((t) => t?.type === 'expense' || t?.type === 'payment')?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
      const savingsRate = income > 0 ? (income - expenses) / income * 100 : 0;

      if (savingsRate >= 10) savingsMonthCount++;

      if (budgets?.length > 0) {
        let allUnderBudget = true;
        budgets?.forEach(budget => {
          const categorySpending = monthTransactions?.filter(t => t?.categoryId === budget?.categoryId)?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
          if (categorySpending > budget?.limitAmount) allUnderBudget = false;
        });
        if (allUnderBudget) budgetAdherenceCount++;
      }
    }

    return [
      {
        title: 'First Transaction',
        description: 'Record your first financial transaction',
        progress: totalTransactions > 0 ? 100 : 0,
        icon: 'CheckCircle2',
        iconColor: 'var(--color-success)',
        isCompleted: totalTransactions > 0,
        nextMilestone: totalTransactions > 0 ? null : 'Add your first transaction'
      },
      {
        title: 'CSV Import Master',
        description: 'Successfully import transactions to track spending',
        progress: totalTransactions >= 10 ? 100 : Math.min(totalTransactions / 10 * 100, 100),
        icon: 'Upload',
        iconColor: 'var(--color-primary)',
        isCompleted: totalTransactions >= 10,
        nextMilestone: totalTransactions >= 10 ? null : `${10 - totalTransactions} more transactions needed`
      },
      {
        title: 'Income Logger',
        description: 'Log income transactions for three consecutive months',
        progress: Math.min(monthsWithIncome / 3 * 100, 100),
        icon: 'TrendingUp',
        iconColor: 'var(--color-accent)',
        isCompleted: monthsWithIncome >= 3,
        nextMilestone: monthsWithIncome >= 3 ? null : `${3 - monthsWithIncome} more months needed`
      },
      {
        title: 'Budget Adherence',
        description: 'Stay within budget for all categories',
        progress: Math.min(budgetAdherenceCount / 3 * 100, 100),
        icon: 'Target',
        iconColor: 'var(--color-warning)',
        isCompleted: budgetAdherenceCount >= 3,
        nextMilestone: budgetAdherenceCount >= 3 ? null : `Stay under budget for ${3 - budgetAdherenceCount} more months`
      },
      {
        title: '10% Saver',
        description: 'Save at least 10% of your income for three months',
        progress: Math.min(savingsMonthCount / 3 * 100, 100),
        icon: 'PiggyBank',
        iconColor: 'var(--color-success)',
        isCompleted: savingsMonthCount >= 3,
        nextMilestone: savingsMonthCount >= 3 ? null : `Save 10% for ${3 - savingsMonthCount} more months`
      },
      {
        title: 'Category Champion',
        description: 'Categorize transactions accurately for better insights',
        progress: Math.min(categorizedTransactions / 100 * 100, 100),
        icon: 'Tags',
        iconColor: 'var(--color-primary)',
        isCompleted: categorizedTransactions >= 100,
        nextMilestone: categorizedTransactions >= 100 ? null : `${100 - categorizedTransactions} more to categorize`
      }
    ];
  };

  const getTopSpendingIncreases = () => {
    let categoryData = getCategoryData();
    return categoryData?.filter(cat => cat?.change > 0)?.sort((a, b) => b?.change - a?.change)?.slice(0, 3);
  };

  const getSpendingPatterns = () => {
    let monthTransactions = transactions?.filter((t) => t?.date?.startsWith(selectedMonth));
    const categoryFrequency = {};

    monthTransactions?.forEach((t) => {
      const categoryName = t?.category || 'Uncategorized';
      if (!categoryFrequency?.[categoryName]) {
        categoryFrequency[categoryName] = 0;
      }
      categoryFrequency[categoryName] += 1;
    });

    const recurring = Object.keys(categoryFrequency)?.filter(cat => categoryFrequency?.[cat] >= 3);
    const oneTime = Object.keys(categoryFrequency)?.filter(cat => categoryFrequency?.[cat] === 1);

    return { recurring, oneTime };
  };

  const summaryData = getSummaryData();
  let categoryData = getCategoryData();
  const subcategoryData = getSubcategoryData();
  const topMerchants = getTopMerchants();
  const trendData = getTrendData();
  const achievements = getAchievements();
  const financialHealth = getFinancialHealthStatus();
  const incomeStability = getIncomeStability();
  const creditUsage = getCreditUsageTrend();
  const topIncreases = getTopSpendingIncreases();
  const spendingPatterns = getSpendingPatterns();

  const filteredTransactions = getReportTransactions();
  const availableCategories = getAvailableCategories();
  const availableSubcategories = getAvailableSubcategories();

  const handleExport = async () => {
    if (!hasProAccess) {
      setShowUpgradeModal(true);
      return;
    }

    const csvContent = [
      ['Category', 'Amount', 'Percentage', 'Change vs Last Month', 'Trend', 'Insight'],
      ...categoryData?.map((item) => [
        item?.name,
        formatCurrency(item?.value, 'BBD'),
        `${item?.percentage?.toFixed(1)}%`,
        `${item?.change?.toFixed(1)}%`,
        item?.trend,
        item?.insight || ''
      ])
    ]?.map((row) => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `financial-insights-${selectedMonth}.csv`;
    link?.click();
    window.URL?.revokeObjectURL(url);
  };

  const exportToPDF = async () => {
    if (!hasProAccess) {
      setShowUpgradeModal(true);
      return;
    }

    try {
      setGenerating(true);
      const reportElement = document.getElementById('report-preview-content');
      
      if (!reportElement) {
        throw new Error('Report preview not found');
      }

      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas?.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas?.height * imgWidth) / canvas?.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf?.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf?.addPage();
        pdf?.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `financial-report-${reportConfig?.dateFrom}-to-${reportConfig?.dateTo}.pdf`;
      pdf?.save(fileName);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert(err?.message || 'Failed to export PDF');
    } finally {
      setGenerating(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Financial Insights', path: '/financial-insights' }
  ];

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Financial Insights - Rovyn Budget</title>
          <meta name="description" content="Pro-only advanced analytics with comprehensive financial analysis, category breakdowns, spending trends, and achievement tracking" />
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="pt-16">
            <div className="mx-auto px-16 md:px-24 lg:px-32 py-24 md:py-32 lg:py-40">
              <div className="flex items-center justify-center py-40">
                <div className="text-center">
                  <svg className="animate-spin h-12 w-12 mx-auto mb-16 text-primary" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-muted-foreground">Loading financial insights...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Financial Insights | Rovyn</title>
        <meta name="description" content="Pro-only advanced analytics with comprehensive financial analysis, category breakdowns, spending trends, and achievement tracking" />
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background pt-20">
        <div className="container mx-auto px-16 md:px-24 lg:px-32 py-24 md:py-32 lg:py-40">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="mb-24 md:mb-32">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-8">
              Financial Insights & Reports
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              {activeTab === 'insights' && 'Analyze your spending patterns and financial health'}
              {activeTab === 'reports' && 'Generate detailed financial reports'}
              {activeTab === 'achievements' && 'Track your financial milestones and achievements'}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="mb-32 flex items-center gap-12 p-4 bg-muted rounded-lg w-fit">
            <button
              onClick={() => setActiveTab('insights')}
              className={`px-16 py-8 rounded-lg font-medium text-sm transition-smooth ${
                activeTab === 'insights' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="TrendingUp" size={16} className="inline mr-8" />
              Insights
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`px-16 py-8 rounded-lg font-medium text-sm transition-smooth ${
                activeTab === 'reports' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="FileText" size={16} className="inline mr-8" />
              Reports
            </button>
            <button
              onClick={() => setActiveTab('achievements')}
              className={`px-16 py-8 rounded-lg font-medium text-sm transition-smooth ${
                activeTab === 'achievements' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Award" size={16} className="inline mr-8" />
              Achievements
            </button>
          </div>

          {/* Insights Tab */}
          {activeTab === 'insights' && (
            <>
              <div className="mb-24 md:mb-32">
                <MonthSelector
                  selectedMonth={selectedMonth}
                  onMonthChange={setSelectedMonth}
                  availableMonths={availableMonths}
                />
              </div>

              {hasProAccess && (
                <div className="mb-32 bg-card rounded-lg p-24 shadow-elevation-2 border-l-4" style={{ borderLeftColor: financialHealth?.color }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-16">
                      <div className="w-48 h-48 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name={financialHealth?.icon} size={24} color={financialHealth?.color} />
                      </div>
                      <div>
                        <h3 className="text-xl font-heading font-semibold text-foreground mb-4">
                          Financial Health: {financialHealth?.status}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {financialHealth?.status === 'On Track' && 'Great job! Your finances are healthy with good savings and budget adherence.'}
                          {financialHealth?.status === 'Caution' && 'Your finances need attention. Review spending and budget allocation.'}
                          {financialHealth?.status === 'Needs Attention' && 'Take action now. Review your budget and reduce unnecessary expenses.'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-16">
                      {incomeStability && (
                        <div className="text-center px-16 py-12 bg-muted rounded-lg">
                          <p className="text-xs text-muted-foreground mb-4">Income Stability</p>
                          <div className="flex items-center gap-8">
                            <Icon name={incomeStability?.icon} size={16} color={incomeStability?.color} />
                            <span className="text-sm font-semibold" style={{ color: incomeStability?.color }}>{incomeStability?.status}</span>
                          </div>
                        </div>
                      )}
                      {creditUsage && (
                        <div className="text-center px-16 py-12 bg-muted rounded-lg">
                          <p className="text-xs text-muted-foreground mb-4">Credit Usage</p>
                          <div className="flex items-center gap-8">
                            <Icon name={creditUsage?.icon} size={16} color={creditUsage?.color} />
                            <span className="text-sm font-semibold" style={{ color: creditUsage?.color }}>
                              {creditUsage?.trend === 'increasing' ? 'Rising' : creditUsage?.trend === 'decreasing' ? 'Improving' : 'Stable'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!hasProAccess && (
                <div className="mb-32 relative">
                  <div className="blur-sm pointer-events-none">
                    <div className="bg-card rounded-lg p-24 shadow-elevation-2 border-l-4 border-success">
                      <div className="flex items-center gap-16">
                        <div className="w-48 h-48 rounded-full bg-success/10 flex items-center justify-center">
                          <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
                        </div>
                        <div>
                          <h3 className="text-xl font-heading font-semibold text-foreground mb-4">Financial Health: On Track</h3>
                          <p className="text-sm text-muted-foreground">Great job! Your finances are healthy...</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Button onClick={() => setShowUpgradeModal(true)} iconName="Unlock" iconPosition="left">
                      Unlock Health Status
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex flex-col lg:flex-row gap-16 lg:gap-20 lg:gap-32">
                <div className="flex-1 flex gap-12">
                  <Button
                    variant="outline"
                    onClick={handleExport}
                    iconName="Download"
                    iconPosition="left"
                    iconSize={18}
                    disabled={categoryData?.length === 0}
                  >
                    Export CSV {!hasProAccess && <Icon name="Lock" size={12} className="ml-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={exportToPDF}
                    iconName="FileText"
                    iconPosition="left"
                    iconSize={18}
                    disabled={categoryData?.length === 0}
                  >
                    Export PDF {!hasProAccess && <Icon name="Lock" size={12} className="ml-4" />}
                  </Button>
                </div>
              </div>

              <div id="insights-report-preview">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 md:gap-20 lg:gap-24 mt-32">
                  {summaryData?.map((card, index) => (
                    <SummaryCard key={index} {...card} isPro={hasProAccess} />
                  ))}
                </div>

                {hasProAccess && topIncreases?.length > 0 && (
                  <div className="mt-32 bg-card rounded-lg p-24 shadow-elevation-2">
                    <div className="flex items-center mb-16">
                      <Icon name="TrendingUp" size={24} color="var(--color-error)" strokeWidth={2.5} />
                      <h3 className="ml-12 text-lg md:text-xl font-heading font-semibold text-card-foreground">
                        Top 3 Spending Increases
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
                      {topIncreases?.map((cat, index) => (
                        <div key={index} className="bg-muted rounded-lg p-16">
                          <div className="flex items-center justify-between mb-8">
                            <span className="text-sm font-semibold text-foreground">{cat?.name}</span>
                            <span className="text-xs px-8 py-4 rounded-full bg-error/10 text-error font-semibold">
                              +{cat?.change?.toFixed(0)}%
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{cat?.insight}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {hasProAccess && (spendingPatterns?.recurring?.length > 0 || spendingPatterns?.oneTime?.length > 0) && (
                  <div className="mt-32 bg-card rounded-lg p-24 shadow-elevation-2">
                    <div className="flex items-center mb-16">
                      <Icon name="Activity" size={24} color="var(--color-primary)" strokeWidth={2.5} />
                      <h3 className="ml-12 text-lg md:text-xl font-heading font-semibold text-card-foreground">
                        Spending Patterns
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                      {spendingPatterns?.recurring?.length > 0 && (
                        <div className="bg-muted rounded-lg p-16">
                          <h4 className="text-sm font-semibold text-foreground mb-8 flex items-center gap-8">
                            <Icon name="Repeat" size={16} color="var(--color-success)" />
                            Recurring Spending
                          </h4>
                          <p className="text-xs text-muted-foreground mb-8">Categories with consistent transactions:</p>
                          <div className="flex flex-wrap gap-8">
                            {spendingPatterns?.recurring?.slice(0, 5)?.map((cat, i) => (
                              <span key={i} className="text-xs px-8 py-4 rounded-full bg-success/10 text-success">{cat}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {spendingPatterns?.oneTime?.length > 0 && (
                        <div className="bg-muted rounded-lg p-16">
                          <h4 className="text-sm font-semibold text-foreground mb-8 flex items-center gap-8">
                            <Icon name="Zap" size={16} color="var(--color-warning)" />
                            One-Time Spikes
                          </h4>
                          <p className="text-xs text-muted-foreground mb-8">Unusual or single transactions:</p>
                          <div className="flex flex-wrap gap-8">
                            {spendingPatterns?.oneTime?.slice(0, 5)?.map((cat, i) => (
                              <span key={i} className="text-xs px-8 py-4 rounded-full bg-warning/10 text-warning">{cat}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {hasProAccess && (
                  <div className="mt-32 flex items-center gap-12">
                    <span className="text-sm font-medium text-muted-foreground">View Period:</span>
                    <div className="flex gap-8">
                      <Button
                        variant={viewPeriod === 'monthly' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewPeriod('monthly')}
                      >
                        Monthly
                      </Button>
                      <Button
                        variant={viewPeriod === 'quarterly' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewPeriod('quarterly')}
                      >
                        Quarterly
                      </Button>
                      <Button
                        variant={viewPeriod === 'yearly' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewPeriod('yearly')}
                      >
                        Year-to-Date
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 md:gap-28 lg:gap-32 mt-32">
                  <CategoryBreakdownChart
                    data={categoryData}
                    currency={getCurrencySymbol('BBD')}
                    isPro={hasProAccess}
                  />

                  <TrendChart
                    data={trendData}
                    currency={getCurrencySymbol('BBD')}
                    chartType={chartType}
                    onChartTypeChange={setChartType}
                    viewPeriod={viewPeriod}
                    isPro={hasProAccess}
                  />
                </div>

                {hasProAccess && categoryData?.length > 0 && (
                  <div className="mt-32 flex items-center gap-12">
                    <span className="text-sm font-medium text-muted-foreground">Sort Categories By:</span>
                    <div className="flex gap-8">
                      <Button
                        variant={categorySortBy === 'highest' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCategorySortBy('highest')}
                      >
                        Highest Spend
                      </Button>
                      <Button
                        variant={categorySortBy === 'growth' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCategorySortBy('growth')}
                      >
                        Fastest Growth
                      </Button>
                      <Button
                        variant={categorySortBy === 'risk' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCategorySortBy('risk')}
                      >
                        Budget Risk
                      </Button>
                    </div>
                  </div>
                )}

                {categoryData?.length > 0 && (
                  <div className="mt-32">
                    <CategoryDataTable
                      data={categoryData}
                      currency={getCurrencySymbol('BBD')}
                      isPro={hasProAccess}
                      showInsights={hasProAccess}
                    />
                  </div>
                )}
              </div>

              <div className="mt-40">
                <div className="flex items-center justify-between mb-24">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground">
                    Your Achievements
                  </h2>
                  {hasProAccess && (
                    <span className="text-sm text-muted-foreground">
                      {achievements?.filter(a => a?.isCompleted)?.length} of {achievements?.length} completed
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20 md:gap-24 lg:gap-28">
                  {achievements?.map((achievement, index) => (
                    <AchievementCard 
                      key={index} 
                      title={achievement.title}
                      description={achievement.description}
                      progress={achievement.progress}
                      icon={achievement.icon}
                      iconColor={achievement.iconColor}
                      isCompleted={achievement.isCompleted}
                      isPro={hasProAccess}
                      showNextMilestone={hasProAccess}
                      nextMilestone={achievement.nextMilestone}
                    />
                  ))}
                  {achievements?.length === 0 && (
                    <div className="col-span-full text-center py-40">
                      <Icon name="Award" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-16" />
                      <p className="text-muted-foreground">No achievements yet. Keep tracking your finances to unlock achievements!</p>
                    </div>
                  )}
                </div>
              </div>

              {transactions?.length === 0 && (
                <div className="mt-40 text-center py-40 bg-card rounded-lg border border-border">
                  <svg className="w-16 h-16 mx-auto mb-16 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <h3 className="text-xl font-heading font-semibold text-foreground mb-8">
                    No Transaction Data
                  </h3>
                  <p className="text-muted-foreground mb-20">
                    Start adding transactions to see your financial insights
                  </p>
                  <Button
                    onClick={() => navigate('/transactions-management')}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Add Transaction
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && hasProAccess && (
            <div className="space-y-24">
              {/* Compact Filter Panel */}
              <div className="bg-card rounded-lg shadow-elevation-2 border border-border">
                <button
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  className="w-full px-20 py-16 flex items-center justify-between hover:bg-muted/50 transition-smooth rounded-t-lg"
                >
                  <div className="flex items-center gap-12">
                    <Icon name="Filter" size={20} color="var(--color-primary)" />
                    <h2 className="text-lg font-heading font-semibold text-card-foreground">Filters</h2>
                    {(reportConfig?.selectedTypes?.length > 0 || reportConfig?.selectedAccounts?.length > 0 || reportConfig?.selectedCategories?.length > 0) && (
                      <span className="text-xs px-8 py-4 rounded-full bg-primary/10 text-primary font-semibold">
                        {reportConfig?.selectedTypes?.length + reportConfig?.selectedAccounts?.length + reportConfig?.selectedCategories?.length} active
                      </span>
                    )}
                  </div>
                  <Icon name={filtersExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
                </button>

                {filtersExpanded && (
                  <div className="px-20 pb-20 space-y-16 border-t border-border">
                    {/* Date Range */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-16">
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-6">From Date</label>
                        <input
                          type="date"
                          value={reportConfig?.dateFrom}
                          onChange={(e) => setReportConfig({ ...reportConfig, dateFrom: e?.target?.value })}
                          className="w-full px-12 py-8 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-6">To Date</label>
                        <input
                          type="date"
                          value={reportConfig?.dateTo}
                          onChange={(e) => setReportConfig({ ...reportConfig, dateTo: e?.target?.value })}
                          className="w-full px-12 py-8 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>

                    {/* Type Filter */}
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-8">
                        Transaction Type {reportConfig?.selectedTypes?.length > 0 && `(${reportConfig?.selectedTypes?.length} selected)`}
                      </label>
                      <div className="flex flex-wrap gap-8">
                        {['income', 'expense', 'payment']?.map(type => (
                          <label key={type} className="flex items-center gap-6 px-12 py-8 bg-background border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-smooth">
                            <input
                              type="checkbox"
                              checked={reportConfig?.selectedTypes?.includes(type)}
                              onChange={(e) => {
                                const newTypes = e?.target?.checked
                                  ? [...reportConfig?.selectedTypes, type]
                                  : reportConfig?.selectedTypes?.filter(t => t !== type);
                                setReportConfig({ 
                                  ...reportConfig, 
                                  selectedTypes: newTypes,
                                  selectedCategories: [],
                                  selectedSubcategories: []
                                });
                              }}
                              className="w-4 h-4 rounded border-border"
                            />
                            <span className="text-sm text-card-foreground capitalize">{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Exclude Transfers Option */}
                    <div className="border-t border-border pt-16">
                      <label className="flex items-center gap-8 cursor-pointer hover:bg-muted/50 p-8 rounded transition-smooth">
                        <input
                          type="checkbox"
                          checked={reportConfig?.excludeTransfers}
                          onChange={(e) => {
                            setReportConfig({ 
                              ...reportConfig, 
                              excludeTransfers: e?.target?.checked
                            });
                          }}
                          className="w-4 h-4 rounded border-border"
                        />
                        <div>
                          <span className="text-sm font-medium text-card-foreground">Exclude Transfers</span>
                          <p className="text-xs text-muted-foreground mt-1">Remove transfer in and transfer out transactions from the report</p>
                        </div>
                      </label>
                    </div>

                    {/* Advanced Filters Accordion */}
                    <div className="border-t border-border pt-16">
                      <button
                        onClick={() => setAdvancedFiltersExpanded(!advancedFiltersExpanded)}
                        className="flex items-center gap-8 text-sm font-medium text-primary hover:text-primary/80 transition-smooth"
                      >
                        <Icon name={advancedFiltersExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                        Advanced Filters
                      </button>

                      {advancedFiltersExpanded && (
                        <div className="mt-16 space-y-16">
                          {/* Accounts Filter */}
                          <div>
                            <label className="block text-sm font-medium text-card-foreground mb-8">
                              Accounts {reportConfig?.selectedAccounts?.length > 0 && `(${reportConfig?.selectedAccounts?.length} selected)`}
                            </label>
                            <div className="max-h-[150px] overflow-y-auto space-y-6 border border-border rounded-lg p-12 bg-background">
                              {accounts?.length > 0 ? (
                                accounts?.map(account => (
                                  <label key={account?.id} className="flex items-center gap-8 cursor-pointer hover:bg-muted/50 p-6 rounded transition-smooth">
                                    <input
                                      type="checkbox"
                                      checked={reportConfig?.selectedAccounts?.includes(account?.id)}
                                      onChange={(e) => {
                                        const newAccounts = e?.target?.checked
                                          ? [...reportConfig?.selectedAccounts, account?.id]
                                          : reportConfig?.selectedAccounts?.filter(id => id !== account?.id);
                                        setReportConfig({ ...reportConfig, selectedAccounts: newAccounts });
                                      }}
                                      className="w-4 h-4 rounded border-border"
                                    />
                                    <span className="text-sm text-card-foreground">{account?.name}</span>
                                  </label>
                                ))
                              ) : (
                                <p className="text-sm text-muted-foreground p-8">No accounts available</p>
                              )}
                            </div>
                          </div>

                          {/* Categories Filter */}
                          <div>
                            <label className="block text-sm font-medium text-card-foreground mb-8">
                              Categories {reportConfig?.selectedCategories?.length > 0 && `(${reportConfig?.selectedCategories?.length} selected)`}
                            </label>
                            {availableCategories?.length > 0 ? (
                              <div className="max-h-[200px] overflow-y-auto space-y-6 border border-border rounded-lg p-12 bg-background">
                                {availableCategories?.map(category => (
                                  <label key={category?.id} className="flex items-center gap-8 cursor-pointer hover:bg-muted/50 p-6 rounded transition-smooth">
                                    <input
                                      type="checkbox"
                                      checked={reportConfig?.selectedCategories?.includes(category?.id)}
                                      onChange={(e) => {
                                        const newCategories = e?.target?.checked
                                          ? [...reportConfig?.selectedCategories, category?.id]
                                          : reportConfig?.selectedCategories?.filter(id => id !== category?.id);
                                        setReportConfig({ 
                                          ...reportConfig, 
                                          selectedCategories: newCategories,
                                          selectedSubcategories: []
                                        });
                                      }}
                                      className="w-4 h-4 rounded border-border"
                                    />
                                    <span className="text-sm text-card-foreground">{category?.name}</span>
                                    <span className="text-xs text-muted-foreground">({category?.type})</span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <div className="border border-border rounded-lg p-12 bg-muted/30">
                                <p className="text-sm text-muted-foreground text-center">
                                  {reportConfig?.selectedTypes?.length > 0 
                                    ? 'No categories found for selected types in this date range' 
                                    : 'Select transaction types above to see available categories'}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Subcategories Filter */}
                          {availableSubcategories?.length > 0 && (
                            <div>
                              <label className="block text-sm font-medium text-card-foreground mb-8">
                                Subcategories {reportConfig?.selectedSubcategories?.length > 0 && `(${reportConfig?.selectedSubcategories?.length} selected)`}
                              </label>
                              <div className="max-h-[150px] overflow-y-auto space-y-6 border border-border rounded-lg p-12 bg-background">
                                {availableSubcategories?.map(subcategory => {
                                  const parent = categories?.find(c => c?.id === subcategory?.parentId);
                                  return (
                                    <label key={subcategory?.id} className="flex items-center gap-8 cursor-pointer hover:bg-muted/50 p-6 rounded transition-smooth">
                                      <input
                                        type="checkbox"
                                        checked={reportConfig?.selectedSubcategories?.includes(subcategory?.id)}
                                        onChange={(e) => {
                                          const newSubcategories = e?.target?.checked
                                            ? [...reportConfig?.selectedSubcategories, subcategory?.id]
                                            : reportConfig?.selectedSubcategories?.filter(id => id !== subcategory?.id);
                                          setReportConfig({ ...reportConfig, selectedSubcategories: newSubcategories });
                                        }}
                                        className="w-4 h-4 rounded border-border"
                                      />
                                      <span className="text-sm text-card-foreground">{subcategory?.name}</span>
                                      <span className="text-xs text-muted-foreground italic">under {parent?.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-12 pt-8">
                      <Button 
                        onClick={() => setReportConfig({
                          ...reportConfig,
                          selectedTypes: [],
                          selectedAccounts: [],
                          selectedCategories: [],
                          selectedSubcategories: []
                        })}
                        variant="outline"
                        size="sm"
                      >
                        Clear Filters
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Report Preview */}
              <div id="report-preview-content" className="bg-card rounded-lg p-16 md:p-24 shadow-elevation-2 border border-border">
                <div className="flex items-center justify-between mb-20">
                  <h2 className="text-xl font-heading font-semibold text-card-foreground">Financial Report</h2>
                  <div className="flex gap-8">
                    <Button onClick={handleExport} variant="outline" size="sm" iconName="Download" iconPosition="left">
                      CSV
                    </Button>
                    <Button onClick={exportToPDF} loading={generating} disabled={generating} size="sm" iconName="FileText" iconPosition="left">
                      PDF
                    </Button>
                  </div>
                </div>

                {filteredTransactions?.length === 0 ? (
                  <div className="text-center py-32 bg-muted/30 rounded-lg">
                    <Icon name="AlertCircle" size={40} className="mx-auto mb-12 text-warning" />
                    <h3 className="text-lg font-semibold text-foreground mb-8">No Data Found</h3>
                    <p className="text-sm text-muted-foreground mb-16">
                      {reportConfig?.selectedTypes?.length === 0 && reportConfig?.selectedCategories?.length === 0 
                        ? 'No transactions found in the selected date range.' :'No transactions match your current filter criteria.'}
                    </p>
                    <div className="text-xs text-muted-foreground space-y-4">
                      <p>Try adjusting your filters:</p>
                      <ul className="list-disc list-inside space-y-2">
                        <li>Expand the date range</li>
                        <li>Select different transaction types</li>
                        <li>Remove category filters</li>
                        <li>Select different accounts</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-24">
                    {/* Summary Cards */}
                    <div>
                      <h3 className="text-base font-semibold text-foreground mb-12">Summary</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
                        {summaryData?.map((card, index) => <SummaryCard key={index} {...card} isPro={true} />)}
                      </div>
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
                      <CategoryBreakdownChart data={categoryData} currency={getCurrencySymbol('BBD')} isPro={true} />
                      <TrendChart data={trendData} currency={getCurrencySymbol('BBD')} chartType={chartType} onChartTypeChange={setChartType} viewPeriod={viewPeriod} isPro={true} />
                    </div>

                    {/* Category Breakdown Table */}
                    {categoryData?.length > 0 && (
                      <div>
                        <h3 className="text-base font-semibold text-foreground mb-12">Category Breakdown</h3>
                        <CategoryDataTable data={categoryData} currency={getCurrencySymbol('BBD')} isPro={true} showInsights={true} />
                      </div>
                    )}

                    {/* Subcategory Breakdown */}
                    {subcategoryData?.length > 0 && (
                      <div>
                        <h3 className="text-base font-semibold text-foreground mb-12">Subcategory Analysis</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted">
                              <tr>
                                <th className="px-12 py-10 text-left font-semibold text-foreground">Subcategory</th>
                                <th className="px-12 py-10 text-left font-semibold text-foreground">Parent Category</th>
                                <th className="px-12 py-10 text-right font-semibold text-foreground">Amount</th>
                                <th className="px-12 py-10 text-right font-semibold text-foreground">Transactions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {subcategoryData?.map((sub, index) => (
                                <tr key={index} className="hover:bg-muted/50 transition-smooth">
                                  <td className="px-12 py-10 text-foreground font-medium">{sub?.name}</td>
                                  <td className="px-12 py-10 text-muted-foreground">{sub?.parent}</td>
                                  <td className="px-12 py-10 text-right font-semibold text-foreground">
                                    {getCurrencySymbol('BBD')}{sub?.value?.toFixed(2)}
                                  </td>
                                  <td className="px-12 py-10 text-right text-muted-foreground">{sub?.transactions}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Top 10 Merchants */}
                    {topMerchants?.length > 0 && (
                      <div>
                        <h3 className="text-base font-semibold text-foreground mb-12">Top 10 Merchants</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted">
                              <tr>
                                <th className="px-12 py-10 text-left font-semibold text-foreground">Rank</th>
                                <th className="px-12 py-10 text-left font-semibold text-foreground">Merchant</th>
                                <th className="px-12 py-10 text-right font-semibold text-foreground">Total Spent</th>
                                <th className="px-12 py-10 text-right font-semibold text-foreground">Transactions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {topMerchants?.map((merchant, index) => (
                                <tr key={index} className="hover:bg-muted/50 transition-smooth">
                                  <td className="px-12 py-10 text-foreground font-semibold">#{index + 1}</td>
                                  <td className="px-12 py-10 text-foreground">{merchant?.name}</td>
                                  <td className="px-12 py-10 text-right font-semibold text-foreground">
                                    {getCurrencySymbol('BBD')}{merchant?.amount?.toFixed(2)}
                                  </td>
                                  <td className="px-12 py-10 text-right text-muted-foreground">{merchant?.transactions}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Filtered Transactions */}
                    <div>
                      <h3 className="text-base font-semibold text-foreground mb-12">
                        Transactions ({filteredTransactions?.length})
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted">
                            <tr>
                              <th className="px-12 py-10 text-left font-semibold text-foreground">Date</th>
                              <th className="px-12 py-10 text-left font-semibold text-foreground">Description</th>
                              <th className="px-12 py-10 text-left font-semibold text-foreground">Category</th>
                              <th className="px-12 py-10 text-left font-semibold text-foreground">Account</th>
                              <th className="px-12 py-10 text-left font-semibold text-foreground">Type</th>
                              <th className="px-12 py-10 text-right font-semibold text-foreground">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {filteredTransactions?.slice(0, 50)?.map((transaction) => {
                              const account = accounts?.find(a => a?.id === transaction?.accountId);
                              const category = categories?.find(c => c?.id === transaction?.categoryId);
                              
                              return (
                                <tr key={transaction?.id} className="hover:bg-muted/50 transition-smooth">
                                  <td className="px-12 py-10 text-foreground">
                                    {new Date(transaction?.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </td>
                                  <td className="px-12 py-10 text-foreground">{transaction?.description || 'No description'}</td>
                                  <td className="px-12 py-10">
                                    <span className="inline-flex items-center gap-6 px-8 py-4 rounded-full text-xs" style={{ backgroundColor: `${category?.color || '#6366f1'}15`, color: category?.color || '#6366f1' }}>
                                      {transaction?.category || 'Uncategorized'}
                                    </span>
                                  </td>
                                  <td className="px-12 py-10 text-muted-foreground">{account?.name || 'Unknown'}</td>
                                  <td className="px-12 py-10">
                                    <span className={`inline-flex items-center gap-4 px-8 py-4 rounded-full text-xs font-medium ${
                                      transaction?.type === 'income' ? 'bg-success/10 text-success' :
                                      transaction?.type === 'expense' ? 'bg-error/10 text-error' :
                                      transaction?.type === 'payment'? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'
                                    }`}>
                                      {transaction?.type?.charAt(0)?.toUpperCase() + transaction?.type?.slice(1)}
                                    </span>
                                  </td>
                                  <td className={`px-12 py-10 text-right font-semibold ${
                                    transaction?.type === 'income' ? 'text-success' : 'text-error'
                                  }`}>
                                    {transaction?.type === 'income' ? '+' : '-'}{getCurrencySymbol('BBD')}{Math.abs(transaction?.amount || 0)?.toFixed(2)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {filteredTransactions?.length > 50 && (
                        <p className="text-xs text-muted-foreground mt-8 text-center">
                          Showing first 50 of {filteredTransactions?.length} transactions
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Achievements Tab */}
          {activeTab === 'achievements' && (
            <div className="space-y-24">
              <div className="bg-card rounded-lg shadow-elevation-2 p-24 border border-border">
                <div className="flex items-center gap-12 mb-20">
                  <Icon name="Award" size={24} color="var(--color-primary)" />
                  <h2 className="text-xl font-heading font-semibold text-card-foreground">Your Achievements</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-24">
                  Track your financial milestones and celebrate your progress
                </p>

                {loading ? (
                  <div className="flex items-center justify-center py-40">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20">
                    {getAchievements()?.map((achievement, index) => (
                      <AchievementCard key={index} achievement={achievement} />
                    ))}
                    {getAchievements()?.length === 0 && (
                      <div className="col-span-full text-center py-40">
                        <Icon name="Award" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-16" />
                        <p className="text-muted-foreground">No achievements yet. Keep tracking your finances to unlock achievements!</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Budget Progress Section */}
              <div className="bg-card rounded-lg shadow-elevation-2 p-24 border border-border">
                <div className="flex items-center gap-12 mb-20">
                  <Icon name="Target" size={24} color="var(--color-primary)" />
                  <h2 className="text-xl font-heading font-semibold text-card-foreground">Budget Progress</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-24">
                  Monitor your budget adherence and spending patterns
                </p>
                {budgets?.length > 0 ? (
                  <div className="space-y-16">
                    {budgets?.map(budget => {
                      const categorySpending = transactions
                        ?.filter(t => t?.date?.startsWith(selectedMonth) && t?.categoryId === budget?.categoryId)
                        ?.reduce((sum, t) => sum + Math.abs(t?.amount || 0), 0);
                      const percentUsed = (categorySpending / budget?.limitAmount) * 100;
                      return (
                        <div key={budget?.id} className="p-16 bg-muted rounded-lg">
                          <div className="flex items-center justify-between mb-8">
                            <span className="text-sm font-medium text-foreground">{budget?.category?.name}</span>
                            <span className="text-sm text-muted-foreground">
                              {formatCurrency(categorySpending, userProfile?.baseCurrency)} / {formatCurrency(budget?.limitAmount, userProfile?.baseCurrency)}
                            </span>
                          </div>
                          <div className="w-full bg-background rounded-full h-8">
                            <div
                              className={`h-8 rounded-full transition-all ${
                                percentUsed >= 100 ? 'bg-destructive' : percentUsed >= 80 ? 'bg-warning' : 'bg-success'
                              }`}
                              style={{ width: `${Math.min(percentUsed, 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-24">
                    <p className="text-muted-foreground">No budgets set for this month</p>
                  </div>
                )}
              </div>

              {/* Goal Tracking Section */}
              <div className="bg-card rounded-lg shadow-elevation-2 p-24 border border-border">
                <div className="flex items-center gap-12 mb-20">
                  <Icon name="Target" size={24} color="var(--color-primary)" />
                  <h2 className="text-xl font-heading font-semibold text-card-foreground">Goal Tracking</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-24">
                  View your progress towards financial goals
                </p>
                <div className="text-center py-24">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/financial-goals')}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    View All Goals
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DashboardQuickActions />
        </div>
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="reports"
        limit={0}
        current={0}
      />
    </>
  );
};

export default FinancialInsights;